import { useActiveKeys } from '@/entities/active-keys/model'
import { BellIcon, CollapsedDotsIcon } from '@/shared/ui/icons'
import { Button } from '@/shared/ui/atoms/button'
import { CustomSelect as Select } from '@/shared/ui/atoms/select'
import { RightSubMenuWrapper } from './style'
import { useSafeNavigate } from '@/shared/lib/safe-navigate'
import { setCookie, getCookie } from '@/shared/lib/formatter'
import Avatar from './avatar'
import { useUserMe } from '@/shared/lib/hooks'
import { FRESHNESS, useQuery, useRequest, useEntityInvalidate } from '@/shared/api'
import { useLanguage } from '@/entities/language/model'
import { useNotificationsStore } from '@/entities/hr/notifications/model'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/entities/auth/model'
import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import Weather from './weather'
import { NotificationPopupModal } from '@/shared/ui/molecules/notification-popup-modal'
import { Badge } from 'antd'

const COOKIE_KEYS = {
  TOKEN: 'token',
} as const

const languageOptions = [
  { value: 'ruRUS', label: 'Рус' },
  { value: 'uzLAT', label: 'O`zb' },
  { value: 'uzCYR', label: 'Ўзб' },
]

interface RightSubMenuProps {
  toggleTheme: (themeName: string) => void
  themeName: string
}

const RightSubMenu = ({ toggleTheme, themeName }: RightSubMenuProps) => {
  const { t, i18n } = useTranslation()
  const navigate = useSafeNavigate()
  const { pathname } = useLocation()
  const isHome = pathname === '/'
  const notifications = useNotificationsStore((s) => s.notifications)
  const totalUnreadMessages = useNotificationsStore((s) => s.totalUnreadMessages)
  const setUserNotifications = useNotificationsStore((s) => s.setNotifications)
  const setTotalUnreadMessages = useNotificationsStore((s) => s.setTotalUnreadMessages)
  const setTotalNotifications = useNotificationsStore((s) => s.setTotalNotifications)
  const { initialActiveKeys } = useActiveKeys()
  const getUserMe = useUserMe()
  const { lang, setLanguage } = useLanguage()
  const { user, logout } = useAuth()
  const tokenFromCookie = getCookie(COOKIE_KEYS.TOKEN)
  const { request } = useRequest()
  const [open, setOpen] = useState(false)
  const rawUserId = getCookie('userId')
  const userId = rawUserId && rawUserId !== 'null' && rawUserId !== 'undefined' ? rawUserId : null

  const themeOptions = [
    { value: 'dark', label: t('dark') },
    { value: 'classic', label: t('classic') },
  ]

  const langCookies = getCookie('lang')
  const handleLanguageChange = (value: string) => {
    setLanguage(value)

    if (tokenFromCookie) {
      getUserMe?.mutate(undefined)
    }
  }
  const isValidUserId = !!userId && userId !== 'null' && userId !== 'undefined'
  const invalidateNotifications = useEntityInvalidate('/v1/notifications')
  const { data: notifData, isFetching } = useQuery<{
    success?: boolean
    data: unknown
    meta: { unreadCount: number; total: number }
  }>(
    ['/v1/notifications', 'UNREAD', lang, userId],
    () =>
      request({
        url: `/v1/notifications?user_id=${userId}&read=UNREAD&page=0&size=10&sort=id,desc`,
      }) as any as Promise<{
        success?: boolean
        data: unknown
        meta: { unreadCount: number; total: number }
      }>,
    {
      enabled: isValidUserId,
      refetchOnWindowFocus: false,
      refetchInterval: 60_000,
      staleTime: FRESHNESS.LOOKUP,
    },
  )

  useEffect(() => {
    if (!notifData?.data) return
    setUserNotifications(
      notifData.data as import('@/entities/hr/notifications/model/types').NotificationItem[],
    )
    setTotalUnreadMessages(notifData?.meta?.unreadCount)
    setTotalNotifications(notifData?.meta?.total)
  }, [notifData, setUserNotifications, setTotalUnreadMessages, setTotalNotifications])

  const modifiedActiveKeys = initialActiveKeys.map((item) => {
    const title = item.title as string | undefined
    if (title === 'Главная') {
      return { ...item, title: t(title) }
    }
    return item
  })
  return (
    <RightSubMenuWrapper>
      <RightSubMenuWrapper.Left>
        {/* <InfiniteIcon /> */}
        <NotificationPopupModal
          open={open}
          onOpenChange={setOpen}
          notifications={
            notifications as any as {
              id: string | number
              message?: string
              read?: boolean
              params?: Record<string, string>
            }[]
          }
          refetch={invalidateNotifications}
        >
          <Badge
            status='warning'
            style={{
              top: '-6px',
              right: '-5px',
              backgroundColor: '#FFA500',
              borderRadius: 0,
            }}
            count={totalUnreadMessages}
          >
            <BellIcon
              width='18'
              height='18'
              color='#212121'
              onClick={() => {
                setOpen(true)
                invalidateNotifications()
              }}
            />
          </Badge>
        </NotificationPopupModal>
        <CollapsedDotsIcon />
        {!tokenFromCookie ? (
          <Button
            rounded={true}
            type='default'
            radius='60px'
            size='medium'
            onClick={() => {
              setCookie(
                'breadcrumb_items',
                JSON.stringify([
                  ...modifiedActiveKeys.map((key) => ({
                    ...key,
                    titleKey: (key.titleKey as string) || (key.title as string) || 'main',
                    title: undefined,
                  })),
                  { titleKey: '/login', path: '/login', originalSegment: 'login', isId: false },
                ]),
                7,
              )
              navigate('/login')
              logout()
            }}
          >
            {t('login')}
          </Button>
        ) : (
          <Avatar user={user} />
        )}
        <div>
          <Select
            width='90px'
            options={languageOptions}
            name='lang'
            aria-label={t('languagechange')}
            placeholder={t('not_selected')}
            value={i18n.language || lang}
            defaultValue={langCookies || 'uzCYR'}
            onChange={(value: string) => handleLanguageChange(value)}
          />
        </div>
        <div>
          <Select
            width='120px'
            options={themeOptions}
            name='theme'
            aria-label={t('theme_select')}
            placeholder={t('not_selected')}
            value={themeName}
            onChange={(value: string) => toggleTheme(value)}
          />
        </div>
      </RightSubMenuWrapper.Left>
      <RightSubMenuWrapper.Right>{isHome && <Weather />}</RightSubMenuWrapper.Right>
    </RightSubMenuWrapper>
  )
}

export default RightSubMenu
